import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../../utils/api';

// Cache for installation data to avoid repeated API calls
// Keyed by appliesTo value to support different product types
const instDataCacheByType = {};
const cachePromiseByType = {};

/**
 * Hook to fetch and manage installation data from the backend
 * @param {string} appliesTo - Filter by product type: 'windows', 'patiodoors', or undefined for all
 * Data structure from API:
 * {
 *   installationTypes: [{ id, label, value, active, sort_order }],
 *   exteriorTypes: [{ id, label, value, active, sort_order }],
 *   brickmoldSizes: [{ id, label, value, active, sort_order }],
 *   brickmoldColors: [{ id, label, value, active, sort_order }],
 *   jambTypes: [{ id, label, value, active, sort_order }],
 *   interiorCasings: [{ id, label, value, active, sort_order }],
 *   interiorCasingSizes: [{ id, label, value, active, sort_order }]
 * }
 */
export const useInstallationData = (appliesTo = 'windows') => {
  const cacheKey = appliesTo || 'all';
  const [data, setData] = useState(instDataCacheByType[cacheKey]);
  const [loading, setLoading] = useState(!instDataCacheByType[cacheKey]);
  const [error, setError] = useState(null);

  const fetchInstallationData = useCallback(async (forceRefresh = false) => {
    // Return cached data if available and not forcing refresh
    if (instDataCacheByType[cacheKey] && !forceRefresh) {
      setData(instDataCacheByType[cacheKey]);
      setLoading(false);
      return instDataCacheByType[cacheKey];
    }

    // If already fetching, wait for that promise
    if (cachePromiseByType[cacheKey] && !forceRefresh) {
      try {
        const result = await cachePromiseByType[cacheKey];
        setData(result);
        setLoading(false);
        return result;
      } catch (e) {
        setError(e.message);
        setLoading(false);
        return null;
      }
    }

    setLoading(true);
    setError(null);

    // Build URL with optional applies_to filter
    const url = appliesTo ? `/installation/?applies_to=${appliesTo}` : '/installation/';

    cachePromiseByType[cacheKey] = authFetch(url)
      .then((result) => {
        instDataCacheByType[cacheKey] = result;
        setData(result);
        setLoading(false);
        return result;
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
        cachePromiseByType[cacheKey] = null;
        throw e;
      });

    return cachePromiseByType[cacheKey];
  }, [appliesTo, cacheKey]);

  useEffect(() => {
    if (!instDataCacheByType[cacheKey]) {
      fetchInstallationData();
    }
  }, [fetchInstallationData, cacheKey]);

  const getInstallationTypes = useCallback(() => {
    if (!data?.installationTypes) return [];
    return data.installationTypes.filter(t => t.active);
  }, [data]);

  const getExteriorTypes = useCallback(() => {
    if (!data?.exteriorTypes) return [];
    return data.exteriorTypes.filter(t => t.active);
  }, [data]);

  const getBrickmoldSizes = useCallback(() => {
    if (!data?.brickmoldSizes) return [];
    return data.brickmoldSizes.filter(t => t.active);
  }, [data]);

  const getBrickmoldColors = useCallback(() => {
    if (!data?.brickmoldColors) return [];
    return data.brickmoldColors.filter(t => t.active);
  }, [data]);

  const getJambTypes = useCallback(() => {
    if (!data?.jambTypes) return [];
    return data.jambTypes.filter(t => t.active);
  }, [data]);

  const getInteriorCasings = useCallback(() => {
    if (!data?.interiorCasings) return [];
    return data.interiorCasings.filter(t => t.active);
  }, [data]);

  const getInteriorCasingSizes = useCallback(() => {
    if (!data?.interiorCasingSizes) return [];
    return data.interiorCasingSizes.filter(t => t.active);
  }, [data]);

  // Clear cache (useful for admin updates)
  const clearCache = useCallback(() => {
    Object.keys(instDataCacheByType).forEach(key => {
      delete instDataCacheByType[key];
    });
    Object.keys(cachePromiseByType).forEach(key => {
      delete cachePromiseByType[key];
    });
  }, []);

  return {
    data,
    loading,
    error,
    refresh: () => fetchInstallationData(true),
    clearCache,
    getInstallationTypes,
    getExteriorTypes,
    getBrickmoldSizes,
    getBrickmoldColors,
    getJambTypes,
    getInteriorCasings,
    getInteriorCasingSizes
  };
};

export default useInstallationData;
